/**
 * Two tones of voice, one set of lines.
 *
 * The game says the same things either way; it just says them differently. A
 * player who has confirmed they are an adult gets the blunt, funnier, sweary
 * version - everyone else gets a version that is safe to leave a child alone
 * with.
 *
 * The shape matters as much as the content:
 *
 *   - the clean wording is the **base** and the coarse one an optional
 *     override, never the other way round. A line written without an `adult`
 *     field simply reads the same in both modes, so the failure mode of
 *     forgetting one is a child-safe line shown to an adult, never the
 *     reverse;
 *   - every key lives in one table, so `LineKey` is a closed union and a typo
 *     is a compile error rather than a silent fallback string;
 *   - the default is `family`, and anything unrecognised parses back to it.
 *
 * Pure data and lookups: no DOM, no storage. ui/ decides which tone is in
 * force and passes it in.
 */

import type { Lang } from "./lang.js";

export type Tone = "family" | "adult";

/** The safe one, always. A missing or unrecognised setting lands here. */
export const DEFAULT_TONE: Tone = "family";

export interface Line {
  /** Shown to everyone. Must always read cleanly. */
  readonly family: string;
  /** Shown only to players who confirmed they are adults. Optional. */
  readonly adult?: string;
  /**
   * The same line in English. Required.
   *
   * Not optional, and not a side table: a line added without one does not
   * compile. The alternative - falling back to the Ukrainian - would put a
   * Ukrainian sentence in the middle of an English game, in front of whoever
   * is playing, with nothing to warn anybody it had happened.
   */
  readonly en: string;
  /** The blunt English, if it differs. Same rule as `adult`. */
  readonly enAdult?: string;
}

/**
 * Everything the game says back to the player, in both tones.
 *
 * Refusals dominate the list because they are the lines a player reads over
 * and over - which makes them the lines most worth making funny.
 */
export const LINES = {
  // ---- ploughing and sowing ----------------------------------------------
  "not-from-above": {
    family: "орати треба згори, не збоку",
    adult: "збоку город не орють, хазяїне",
    en: "plough from above, not from the side",
    enAdult: "you don't plough a field sideways, farmer",
  },
  "not-soil": {
    family: "тут не зорати — потрібна земля, гола чи з травою, або чорнозем",
    adult: "камінь ти не зореш, хоч трісни",
    en: "nothing to plough here - needs earth, grass or black soil",
    enAdult: "you'll not plough stone, try as you might",
  },
  "not-farmland": {
    family: "сіяти можна лише в ріллю",
    adult: "сіють у ріллю, а не абикуди",
    en: "seed goes in ploughed ground",
    enAdult: "sow it in the furrow, not just anywhere",
  },
  "underwater": {
    family: "під водою не орати",
    adult: "воно ж під водою, ти б ще в морі поле розбив",
    en: "no ploughing under water",
    enAdult: "it's under water. why not plough the sea while you're at it",
  },
  "occupied": {
    family: "згори щось стоїть",
    adult: "згори щось стоїть — прибери спершу",
    en: "something is standing on it",
    enAdult: "something's on top - shift it first",
  },

  // ---- hanging and standing things ---------------------------------------
  "no-room": {
    family: "тут не стане — треба два блоки заввишки",
    adult: "не влізе, тут стелю треба вищу",
    en: "won't fit - needs two blocks of headroom",
    enAdult: "won't fit. you'd need a higher ceiling",
  },
  "no-support": {
    family: "двері нема на що поставити",
    adult: "двері в повітрі не тримаються, хазяїне",
    en: "nothing to hang the door on",
    enAdult: "doors don't hang in mid-air, farmer",
  },
  "nothing-to-hang-on": {
    family: "віконниці треба вішати на стіну",
    adult: "на що ти їх вішати будеш, на хмару?",
    en: "shutters go on a wall",
    enAdult: "hang them on what, a cloud?",
  },
  "no-floor": {
    family: "ліжку треба підлога під обома половинами",
    adult: "півліжка над прірвою — ну таке собі",
    en: "a bed needs floor under both halves",
    enAdult: "half a bed over a drop - bold",
  },
  "rushnyk-occupied": {
    family: "тут зайнято",
    adult: "тут уже щось висить",
    en: "something is there already",
    enAdult: "there's already something hanging there",
  },
  "rushnyk-wall": {
    family: "рушник вішають на стіну",
    adult: "рушник на стіну, а не в порожнечу",
    en: "a rushnyk hangs on a wall",
    enAdult: "on a wall, not into thin air",
  },

  // ---- pail and vat -------------------------------------------------------
  "nothing-to-fill": {
    family: "нема чого набирати",
    adult: "відром повітря не наносиш",
    en: "nothing to fill it with",
    enAdult: "you'll not carry air in a pail",
  },
  "no-space": {
    family: "немає місця у торбі",
    adult: "торба тріщить, хазяїне",
    en: "no room in your bag",
    enAdult: "the bag's fit to burst, farmer",
  },
  "vat-empty": {
    family: "чан порожній — принеси води відром",
    adult: "порожній чан. води принеси, відро для чого?",
    en: "the vat is empty - fetch water with a pail",
    enAdult: "empty vat. fetch water - what's the pail for?",
  },
  "pour-into-vat": {
    family: "лити треба в порожній чан",
    adult: "лий у чан, а не собі під ноги",
    en: "pour it into an empty vat",
    enAdult: "into the vat, not over your own boots",
  },
  "vat-filled": {
    family: "чан повний",
    adult: "чан повний по вінця",
    en: "the vat is full",
    enAdult: "full to the brim",
  },

  // ---- sleeping -----------------------------------------------------------
  "slept": {
    family: "на добраніч. тут ти й прокинешся",
    adult: "спи, хазяїне. тут і прокинешся",
    en: "goodnight. you'll wake here",
    enAdult: "sleep, farmer. you'll wake right here",
  },
  "spawn-set": {
    family: "тут ти прокинешся. спати можна вночі",
    adult: "вдень не спиться. лягай як стемніє — тут і прокинешся",
    en: "you'll wake here. sleeping is for the night",
    enAdult: "no sleeping by day. lie down after dark - you'll wake here",
  },

  // ---- aiming -------------------------------------------------------------
  "no-arrows": {
    family: "стріли скінчились",
    adult: "стріл нема, чим стріляти будеш?",
    en: "out of arrows",
    enAdult: "no arrows. what were you planning to shoot with?",
  },
  "need-pickaxe": {
    family: "тут потрібне кайло",
    adult: "голими руками камінь не візьмеш",
    en: "this needs a pickaxe",
    enAdult: "you'll not take stone with your bare hands",
  },
  "need-shovel": {
    family: "тут потрібна лопата",
    adult: "лопатою копай, не пальцями",
    en: "this needs a shovel",
    enAdult: "dig with a shovel, not your fingers",
  },
  "need-sabre": {
    family: "тут потрібна шабля",
    adult: "шаблею, хазяїне",
    en: "this needs a sabre",
    enAdult: "the sabre, farmer",
  },
  "nothing-there": {
    family: "тут немає куди його поставити",
    adult: "куди ти його ставиш? тут нема на що",
    en: "there is nowhere to put it here",
    enAdult: "put it where? there is nothing there",
  },
  "round-closed": {
    family: "раунд скінчився — світ закрито",
    adult: "все, забіг закінчено. світ прибрали",
    en: "the round is over - this world is closed",
    enAdult: "that is the race run. world's gone",
  },
  "in-tank": {
    family: "ти в танку. WASD — їхати, ЛКМ — вогонь, Shift — вийти",
    adult: "в танку. газ — WASD, гармата — ЛКМ, Shift — назовні",
    en: "you are in the tank. WASD drives, left click fires, Shift gets out",
    enAdult: "in the tank. WASD to drive, click to fire, Shift to bail out",
  },
  "no-shells": {
    family: "снарядів немає",
    adult: "чим стріляти будеш? снарядів катма",
    en: "no shells",
    enAdult: "and what will you fire? there are no shells",
  },
  "tank-gone": {
    family: "танк вибухнув!",
    adult: "танку кінець. вилазь",
    en: "the tank is gone!",
    enAdult: "that is the end of the tank. out you get",
  },
  "war-begins": {
    family: "стіна впала — бій почався!",
    adult: "стіна впала. вперед, хлопці",
    en: "the wall is down - the battle is on!",
    enAdult: "wall's down. go on then",
  },
  "need-iron-pick": {
    family: "адамантій бере тільки залізне кайло",
    adult: "дерев'яним по адамантію? залізне неси",
    en: "adamantium yields only to an iron pickaxe",
    enAdult: "wood against adamantium? fetch the iron one",
  },
  "need-adamant": {
    family: "це ламає тільки адамантієве кайло",
    adult: "без адамантію тут робити нічого",
    en: "only an adamantium pickaxe breaks this",
    enAdult: "nothing to do here without adamantium",
  },
  "unbreakable": {
    family: "цей блок не ламається",
    adult: "цей блок тобі не по зубах",
    en: "this block does not break",
    enAdult: "this one is not for your teeth",
  },
  "need-axe": {
    family: "тут потрібна сокира",
    adult: "сокирою, хазяїне",
    en: "this needs an axe",
    enAdult: "use the axe",
  },
  "need-hoe": {
    family: "тут потрібна сапка",
    adult: "сапка потрібна",
    en: "this needs a hoe",
    enAdult: "you need a hoe",
  },

  "terrain-changed": {
    family: "земля змінилась — почато новий світ. старий у резервному слоті",
    adult: "земля вже не та. почали з чистого, старе в резерві",
    en: "the land has changed - a new world was started. the old one is in the backup slot",
    enAdult: "the land's not the same. started clean, the old one's in the backup",
  },
  "built": {
    family: "хата стала!",
    adult: "от і хата. живи",
    en: "the хата is up!",
    enAdult: "there's your хата. live in it",
  },
  "build-no-room": {
    family: "тут не стане — заважає",
    adult: "не влізе, розчисти спершу",
    en: "won't stand here - something's in the way",
    enAdult: "won't fit. clear the ground first",
  },
  /*
   * Said after the fact, not before.
   *
   * A building that clears everything cannot ask permission each time without
   * becoming the thing that was too tiresome to use. So it goes up, and then
   * it tells you what it cost - with the number, because "some blocks" is not
   * something anybody can act on.
   */
  "built-over": {
    family: "стало. знесло твоїх блоків:",
    adult: "стало. твоєї роботи пішло блоків:",
    en: "up it goes. blocks of yours it took:",
    enAdult: "up it goes. of your own work it took:",
  },
  "build-off-map": {
    family: "тут край світу, хату нема куди ставити",
    adult: "далі краю не забудуєшся",
    en: "that's the edge of the world, nowhere to put it",
    enAdult: "you'll not build past the edge",
  },
  "cast": {
    family: "закинув. тепер чекай",
    adult: "закинув. сиди й не сопи",
    en: "cast. now wait",
    enAdult: "cast. sit still and don't fidget",
  },
  "bite": {
    family: "клює! тягни!",
    adult: "клює, тягни!",
    en: "a bite! pull!",
    enAdult: "it's biting, pull!",
  },
  "fish-lost": {
    family: "зірвалась...",
    adult: "зірвалась, зараза",
    en: "it got away...",
    enAdult: "got away, damn it",
  },
  "caught-fish": {
    family: "карась!",
    adult: "о, карась. буде юшка",
    en: "a crucian!",
    enAdult: "well now, a crucian. that's soup",
  },
  "pulled-early": {
    family: "рано смикнув",
    adult: "рано смикнув, тепер заново",
    en: "too early",
    enAdult: "pulled too early. start again",
  },
  "no-water": {
    family: "тут нема куди закидати",
    adult: "по суші вудкою не ловлять",
    en: "nowhere to cast here",
    enAdult: "you don't fish on dry land",
  },
  "ate": {
    family: "смачно!",
    adult: "от тепер інша розмова",
    en: "good!",
    enAdult: "now that's more like it",
  },
  "not-hungry": {
    family: "сила й так повна",
    adult: "куди в тебе лізе, ти цілий",
    en: "you're at full strength already",
    enAdult: "where would you even put it, you're whole",
  },
  "raid-warning": {
    family: "орки збираються! наліт за хвилину",
    adult: "орки шикуються. хвилина — і будуть тут",
    en: "the orcs are gathering! a raid in a minute",
    enAdult: "the orcs are forming up. one minute and they're here",
  },
  "raid-begins": {
    family: "наліт почався!",
    adult: "почалось. тримайся",
    en: "the raid has begun!",
    enAdult: "here we go. hold on",
  },
  "lair-broken": {
    family: "лігво зруйноване!",
    adult: "одним лігвом менше",
    en: "the lair is destroyed!",
    enAdult: "one lair down",
  },
  "raids-over": {
    family: "останнє лігво впало. нальотів більше не буде",
    adult: "все, лігва скінчились. можна видихнути",
    en: "the last lair has fallen. no more raids",
    enAdult: "that's the lairs done. you can breathe",
  },
  "orc-hit": {
    family: "орк дістав тебе!",
    adult: "орк тебе дістав, зараза",
    en: "an orc got you!",
    enAdult: "the orc got you, the swine",
  },
  "player-hit": {
    family: "тебе вдарили!",
    adult: "тебе віддубасили, от же ж",
    en: "somebody hit you!",
    enAdult: "somebody just clocked you",
  },
  "fell-hard": {
    family: "боляче!",
    adult: "ай, бляха, ноги",
    en: "that hurt!",
    enAdult: "ah, bloody hell, my legs",
  },
  "boat-taken": {
    family: "хтось уже в тому човні",
    adult: "зайнято, шукай інший",
    en: "somebody is already in that boat",
    enAdult: "taken. find another one",
  },
  "launched": {
    family: "човен на воді. сідай — ПКМ",
    adult: "човен на воді. лізь у нього",
    en: "the boat is afloat. right-click to get in",
    enAdult: "boat's in the water. get in it",
  },
  "aboard": {
    family: "пливи. Shift — вийти на берег",
    adult: "веслуй. Shift — і ти на березі",
    en: "row away. Shift to step ashore",
    enAdult: "row, then. Shift to get out",
  },
  "no-bank": {
    family: "нема куди вийти",
    adult: "а виходити куди? кругом вода",
    en: "nowhere to step out",
    enAdult: "step out where? it's all water",
  },
  "tamed": {
    family: "собака тепер твій. він піде за тобою",
    adult: "усе, пес твій. тепер не відчепиться",
    en: "the dog is yours now. it will follow you",
    enAdult: "right, the dog's yours. you'll not shake it now",
  },
  "already-yours": {
    family: "він уже твій",
    adult: "та він і так твій",
    en: "it is already yours",
    enAdult: "it's yours already",
  },
  "lit-fire": {
    family: "зайнялося",
    adult: "о, взялося",
    en: "it caught",
    enAdult: "there we go, it's caught",
  },
  "wont-catch": {
    family: "це не горить",
    adult: "воно не горить, хоч ти що",
    en: "that will not catch",
    enAdult: "that's not going to catch, mate",
  },
  "nothing-to-light": {
    family: "нема чого підпалювати",
    adult: "та тут нема чого палити",
    en: "nothing to light",
    enAdult: "nothing here to light",
  },
  "burnt": {
    family: "пече! вилазь із лави!",
    adult: "горить усе, вилазь звідти",
    en: "it burns! get out of the lava!",
    enAdult: "you're on fire, get out of there",
  },
  "died": {
    family: "тебе вбито. речі лишились там, де ти впав",
    adult: "отак і вбили. манатки лежать там, де ти ліг",
    en: "you were killed. your things are where you fell",
    enAdult: "killed, then. your kit's lying where you went down",
  },

  /*
   * Told, because otherwise it looks like theft.
   *
   * There was nothing under you to catch the bag - a shaft, or the edge of
   * the map - so it is waiting where you wake instead. Without a line, going
   * back to the spot and finding bare ground reads as the game having eaten
   * everything.
   */
  "died-lost": {
    family: "падати не було куди — речі чекають там, де ти прокинувся",
    adult: "там не було дна, тож манатки чекають коло ліжка",
    en: "there was nothing to fall onto - your things are waiting where you woke",
    enAdult: "no bottom down there, so your kit's back by the bed",
  },

  // The drone, going up and coming down.
  "drone-up": {
    family: "дрон у повітрі. G — повернутись у своє тіло, він лишиться літати",
    adult: "полетів. G — назад у власну шкуру, дрон висітиме сам",
    en: "the drone is up. G to come back to your own body; it stays flying",
    enAdult: "up it goes. G to get back in your own skin - it keeps flying",
  },
  "drone-empty": {
    family: "снаряди скінчились. Сідай і бери ще",
    adult: "нічим кидати. Сідай і набирай",
    en: "out of bolts. Land and fetch more",
    enAdult: "nothing left to drop. Land and load up",
  },
  "drone-flat": {
    family: "батарея сіла. Дрон лежить там, де впав",
    adult: "батарея здохла. Дрон валяється там, де впав",
    en: "the battery is flat. The drone is lying where it came down",
    enAdult: "battery's dead. It's lying where it dropped",
  },
  "drone-left": {
    family: "ти вийшов з керування. Дрон лишився в небі — бери пульт",
    adult: "вийшов з керування. Дрон висить там, де був. Пульт у руки",
    en: "you have left the controls. The drone is still up - take the handset",
    enAdult: "off the sticks. It's still up there. Grab the handset",
  },
  "drone-none": {
    family: "жодного дрона в небі. Спершу пусти його",
    adult: "нема чим керувати. Спершу пусти дрона",
    en: "there is no drone up. Send one up first",
    enAdult: "nothing up there to fly. Send one up first",
  },
  "drone-down": {
    family: "дрон опустився. Забери його й постав нову батарею",
    adult: "дрон униз. Підбери й тицьни нову батарею",
    en: "the drone is down. Fetch it and put a new cell in",
    enAdult: "drone's down. Go get it and shove a new cell in",
  },

  "died-kept": {
    family: "тебе вбито. речі при тобі",
    adult: "вбили, але манатки при тобі",
    en: "you were killed. your things are still on you",
    enAdult: "killed, but you kept your kit",
  },
  "keep-on": {
    family: "речі лишатимуться при тобі",
    en: "your things will stay with you",
  },
  "keep-off": {
    family: "речі випадатимуть там, де ти загинеш",
    en: "your things will drop where you die",
  },
  "creative-on": {
    family: "режим творця: у речах відкрилася комора",
    adult: "режим творця. бери з комори що хочеш",
    en: "creative mode: a storeroom has opened in your bag",
    enAdult: "creative mode. take what you like from the storeroom",
  },
  "creative-off": {
    family: "комора зачинена, копай сам",
    adult: "комору зачинено. далі лопатою",
    en: "the storeroom is shut, dig for it yourself",
    enAdult: "storeroom's shut. back to the shovel",
  },

  // ---- letting a creature out (режим творця) ------------------------------
  "spawned": {
    family: "є!",
    adult: "от і воно",
    en: "there it is!",
    enAdult: "there you go",
  },
  "spawn-needs-water": {
    family: "рибі треба вода",
    adult: "риба у воді живе, хазяїне",
    en: "a fish needs water",
    enAdult: "fish live in water, farmer",
  },
  "spawn-needs-ground": {
    family: "потрібна тверда земля",
    adult: "постав його на землю, не в повітря",
    en: "it needs solid ground",
    enAdult: "put it on the ground, not in mid-air",
  },
  // ---- король гори --------------------------------------------------------
  "boom": {
    family: "ой! вибухоблок",
    adult: "ну от, доїхали",
    en: "the block went off!",
    enAdult: "well, that one was live",
  },
  "crown-yours": {
    family: "ти нагорі!",
    adult: "ти нагорі — тримайся",
    en: "you are on top!",
    enAdult: "you're on top - hold it",
  },
  "spawn-not-yours": {
    family: "тут це не твоє",
    adult: "живність на цьому світі сервер водить, не ти",
    en: "that is not yours here",
    enAdult: "what walks on this world is the server's, not yours",
  },
  "spawn-occupied": {
    family: "тут нема місця",
    adult: "тут тісно, посунься",
    en: "there is no room there",
    enAdult: "no room there, shift along",
  },

  "nothing-aimed": {
    family: "нема на що",
    adult: "ти в небо тицяєш",
    en: "nothing there",
    enAdult: "you're poking at the sky",
  },

  "somebody-there": {
    family: "там хтось стоїть",
    adult: "там людина стоїть, ти шо",
    en: "somebody is standing there",
    enAdult: "there's a person standing there, come on",
  },

  // ---- the neighbours -----------------------------------------------------
  /**
   * What дядько Гриць says when you hit him.
   *
   * He only exists when the 18+ switch is on, so the family wording below is
   * never actually shown - it is written anyway, because the rule of this file
   * is that every line has a clean version. The day a village turns up in the
   * children's game, it already has a voice.
   */
  "villager-hit": {
    family: "ой! ти чого штовхаєшся?",
    adult: "ніхуя собі блять, ще й в'їбав",
    en: "ow! what are you shoving for?",
    enAdult: "what the fuck was that, you hit me",
  },
  "villager-hit-boy": {
    family: "ай! я ж нічого не робив",
    adult: "ай! ти чого, дядьку?",
    en: "ow! I wasn't doing anything",
    enAdult: "ow! what was that for, mister?",
  },
  "villager-hit-girl": {
    family: "ой! я мамі скажу",
    adult: "ой! от мамі скажу",
    en: "ow! I'm telling my mother",
    enAdult: "ow! I'm telling my mother",
  },
  "villager-hit-her": {
    family: "ой! а ну не чіпай",
    adult: "ах ти ж холера! руки прибери",
    en: "ow! hands off",
    enAdult: "you little shit! get your hands off",
  },

  // ---- saving -------------------------------------------------------------
  "save-failed": {
    family: "не вдалося зберегти",
    adult: "не збереглося, холера",
    en: "could not save",
    enAdult: "didn't save, damn it",
  },
  "export-failed": {
    family: "не вдалося експортувати",
    adult: "експорт не вийшов, буває",
    en: "could not export",
    enAdult: "export didn't work. it happens",
  },
  "write-failed": {
    family: "не вдалося записати збереження",
    adult: "не записалось, хай йому грець",
    en: "could not write the save",
    enAdult: "wouldn't write. hell with it",
  },
  "no-storage": {
    family: "сховище недоступне",
    adult: "браузер не дає зберігати, зараза",
    en: "storage is not available",
    enAdult: "the browser won't let us save, the swine",
  },
  "tone-adult": {
    family: "18+ увімкнено",
    adult: "18+ увімкнено — тепер без цензури",
    en: "18+ on",
    enAdult: "18+ on - no more censor",
  },
  "tone-family": {
    family: "18+ вимкнено — гра знову добра й без лайки",
    en: "18+ off - the game is kind and clean again",
  },
} as const satisfies Record<string, Line>;

export type LineKey = keyof typeof LINES;

/** Say one line in the given tone, falling back to the clean wording. */
/**
 * What the game says, in the tone and the language in force.
 *
 * Two independent axes. Falling back within a language is fine - a line with
 * no blunt version simply reads the same to an adult - but never *across*
 * one: an English game with no English line would have to show Ukrainian, and
 * the type makes that impossible rather than handling it.
 */
export function say(key: LineKey, tone: Tone, lang: Lang = "uk"): string {
  const line: Line = LINES[key];
  if (lang === "en") return tone === "adult" ? line.enAdult ?? line.en : line.en;
  return tone === "adult" ? line.adult ?? line.family : line.family;
}

/** Read a stored setting. Anything unexpected is treated as family. */
export function parseTone(stored: string | null): Tone {
  return stored === "adult" ? "adult" : DEFAULT_TONE;
}
